import { useEffect, useState, useMemo, type CSSProperties } from "react";
import { useSearchParams } from "react-router-dom";
import {
  listAdminReviews,
  moderateAdminReview,
  respondToAdminReview,
  listGoogleReviews,
  getGoogleReviewStats,
  syncGoogleReviews,
  generateGoogleReviewDraft,
  sendGoogleReviewReply,
  type DistilleryReview,
  type GoogleReview,
  type GoogleReviewStats,
} from "../../lib/api";
import GuestStudioTabs from "../../components/admin/GuestStudioTabs";

function renderStars(rating: number) {
  const full = Math.min(5, Math.max(0, Math.round(rating)));
  return "★".repeat(full) + "☆".repeat(5 - full);
}

type ReviewSource = "guest" | "google";

export default function AdminReviews() {
  const [params, setParams] = useSearchParams();
  // ?tab=google is the deep link the Library's old /reviews page redirects to.
  const source: ReviewSource = params.get("tab") === "google" ? "google" : "guest";
  const setSource = (s: ReviewSource) => {
    const next = new URLSearchParams(params);
    if (s === "google") next.set("tab", "google");
    else next.delete("tab");
    setParams(next, { replace: true });
  };

  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Badge keeps its pre-tab meaning: count of loaded guest (distillery) reviews.
  const [guestCount, setGuestCount] = useState<number | undefined>(undefined);

  return (
    <div style={styles.container}>
      <GuestStudioTabs activeTab="reviews" badgeCounts={{ reviews: guestCount }} />
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Reviews (Guest &amp; Google)</h1>
          <p style={styles.subtitle}>
            {source === "google"
              ? "Sync Google Business reviews and post AI-assisted public replies."
              : "Moderate guest reviews, flag policy violations, and publish responses."}
          </p>
        </div>
      </header>

      <div style={styles.sourceTabs} role="tablist" aria-label="Review source">
        {(
          [
            ["guest", "Guest Reviews"],
            ["google", "Google Reviews"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={source === id}
            tabIndex={source === id ? 0 : -1}
            onKeyDown={(e) => {
              // Two tabs: either arrow moves to the other one (L27).
              if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
              e.preventDefault();
              const other: ReviewSource = id === "guest" ? "google" : "guest";
              setSource(other);
              (e.currentTarget.parentElement?.querySelector(`[data-source="${other}"]`) as HTMLElement | null)?.focus();
            }}
            data-source={id}
            style={{ ...styles.sourceTab, ...(source === id ? styles.sourceTabActive : {}) }}
            onClick={() => setSource(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "ok" ? "rgba(39, 174, 96, 0.15)" : "rgba(224, 80, 64, 0.15)",
            borderColor: toast.type === "ok" ? "#27ae60" : "var(--red)",
            color: toast.type === "ok" ? "#86efac" : "var(--red)",
          }}
        >
          {toast.text}
        </div>
      )}

      {source === "google" ? (
        <GoogleReviewsTab showToast={showToast} />
      ) : (
        <GuestReviewsTab showToast={showToast} onCount={setGuestCount} />
      )}
    </div>
  );
}

type ShowToast = (text: string, type?: "ok" | "err") => void;

function GuestReviewsTab({ showToast, onCount }: { showToast: ShowToast; onCount: (n: number) => void }) {
  const [reviews, setReviews] = useState<DistilleryReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Response Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<DistilleryReview | null>(null);
  const [responseText, setResponseText] = useState("");
  const [submittingResponse, setSubmittingResponse] = useState(false);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const data = await listAdminReviews(statusFilter);
      setReviews(data || []);
      onCount((data || []).length);
    } catch (e: any) {
      showToast(e.message || "Failed to load reviews", "err");
    } finally {
      setLoading(false);
    }
  };

  // Escape listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && modalOpen) setModalOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen]);

  useEffect(() => {
    loadReviews();
  }, [statusFilter]);

  const filteredReviews = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reviews;
    return reviews.filter(
      (r) =>
        (r.distilleries?.name && r.distilleries.name.toLowerCase().includes(q)) ||
        (r.customers?.display_name && r.customers.display_name.toLowerCase().includes(q)) ||
        (r.review_text && r.review_text.toLowerCase().includes(q))
    );
  }, [reviews, searchQuery]);

  const handleModerate = async (reviewId: string, status: "published" | "flagged" | "hidden") => {
    try {
      await moderateAdminReview(reviewId, status);
      showToast(`Review marked as ${status}`);
      await loadReviews();
    } catch (e: any) {
      showToast(e.message || "Action failed", "err");
    }
  };

  const handleOpenResponseModal = (review: DistilleryReview) => {
    setSelectedReview(review);
    setResponseText(review.distillery_response || "");
    setModalOpen(true);
  };

  const handleSubmitResponse = async () => {
    if (!selectedReview || !responseText.trim()) return;
    setSubmittingResponse(true);
    try {
      await respondToAdminReview(selectedReview.id, responseText.trim());
      showToast("✓ Response posted successfully");
      setModalOpen(false);
      await loadReviews();
    } catch (e: any) {
      showToast(e.message || "Failed to post response", "err");
    } finally {
      setSubmittingResponse(false);
    }
  };

  return (
    <>
      {/* Filter and Search Bar */}
      <div style={styles.toolbar}>
        <div style={styles.filterPills}>
          {["all", "published", "flagged", "hidden"].map((status) => (
            <button
              key={status}
              style={{
                ...styles.pillBtn,
                ...(statusFilter === status ? styles.pillBtnActive : {}),
              }}
              onClick={() => setStatusFilter(status)}
            >
              {status.toUpperCase()}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search by reviewer, distillery, or keywords..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Review List */}
      {loading ? (
        <div style={styles.loader}>Loading customer reviews…</div>
      ) : (
        <div style={styles.list}>
          {filteredReviews.map((r) => {
            const author = r.customers?.display_name || "Anonymous Guest";
            const distillery = r.distilleries?.name || "The Sentinel";
            const dateStr = new Date(r.created_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            });

            return (
              <div
                key={r.id}
                style={{
                  ...styles.card,
                  borderLeft:
                    r.status === "published"
                      ? "3px solid #27ae60"
                      : r.status === "flagged"
                      ? "3px solid var(--red, #FF8A7A)"
                      : "3px solid var(--muted, #A39D98)",
                }}
              >
                <div style={styles.cardTop}>
                  <div>
                    <span style={styles.stars}>{renderStars(r.rating)}</span>
                    <strong style={styles.distilleryName}>{distillery}</strong>
                  </div>
                  <div style={styles.metaRight}>
                    <span
                      style={{
                        ...styles.statusBadge,
                        background:
                          r.status === "published"
                            ? "rgba(39, 174, 96, 0.15)"
                            : r.status === "flagged"
                            ? "rgba(224, 80, 64, 0.15)"
                            : "rgba(163, 157, 152, 0.15)",
                        color:
                          r.status === "published"
                            ? "#86efac"
                            : r.status === "flagged"
                            ? "var(--red)"
                            : "var(--muted)",
                      }}
                    >
                      {r.status}
                    </span>
                    <span style={styles.date}>{dateStr}</span>
                  </div>
                </div>

                <div style={styles.byline}>
                  By {author}
                  {r.visit_date && ` · Visited ${new Date(r.visit_date).toLocaleDateString()}`}
                  {r.helpful_count > 0 && ` · 👍 ${r.helpful_count}`}
                </div>

                {r.review_text && <p style={styles.reviewBody}>{r.review_text}</p>}

                {r.distillery_response && (
                  <div style={styles.responseBubble}>
                    <div style={styles.responseHeader}>Response from Team:</div>
                    <div style={styles.responseText}>{r.distillery_response}</div>
                  </div>
                )}

                <div style={styles.actionsRow}>
                  {r.status !== "published" && (
                    <button
                      style={styles.btnPublish}
                      onClick={() => handleModerate(r.id, "published")}
                    >
                      Publish
                    </button>
                  )}
                  {r.status !== "flagged" && (
                    <button
                      style={styles.btnFlag}
                      onClick={() => handleModerate(r.id, "flagged")}
                    >
                      Flag
                    </button>
                  )}
                  {r.status !== "hidden" && (
                    <button
                      style={styles.btnHide}
                      onClick={() => handleModerate(r.id, "hidden")}
                    >
                      Hide
                    </button>
                  )}
                  <button
                    style={styles.btnRespond}
                    onClick={() => handleOpenResponseModal(r)}
                  >
                    {r.distillery_response ? "Edit Response" : "Respond"}
                  </button>
                </div>
              </div>
            );
          })}
          {filteredReviews.length === 0 && (
            <div style={styles.emptyState}>No reviews found matching your search.</div>
          )}
        </div>
      )}

      {/* Response Modal */}
      {modalOpen && selectedReview && (
        <div style={styles.modalBackdrop} onClick={() => setModalOpen(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                Reply to {selectedReview.customers?.display_name || "Guest"}
              </h2>
              <button style={styles.modalClose} onClick={() => setModalOpen(false)}>
                &times;
              </button>
            </div>

            <div style={styles.originalReviewQuote}>
              <div style={styles.stars}>{renderStars(selectedReview.rating)}</div>
              <div style={styles.quoteText}>"{selectedReview.review_text || "(No review body)"}"</div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={styles.label}>Official Response</label>
            </div>

            <textarea
              rows={6}
              placeholder="Write an appreciative, hospitable response..."
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              style={styles.textarea}
            />

            <div style={styles.modalFooter}>
              <button
                style={styles.btnSecondary}
                onClick={() => setModalOpen(false)}
                disabled={submittingResponse}
              >
                Cancel
              </button>
              <button
                style={styles.btnPrimary}
                onClick={handleSubmitResponse}
                disabled={submittingResponse || !responseText.trim()}
              >
                {submittingResponse ? "Posting..." : "Post Response"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Google Business Profile reviews ─────────────────────────────────────────
// Port of the Library's /reviews page (public/js/reviews.js). No Q&A: Google
// retired the Business Profile Q&A API on 2025-11-03 and the Library removed it.

const GOOGLE_REPLY_MAX = 4096; // Google's reply length limit; reply-review enforces it too
const BATCH_DRAFT_LIMIT = 10;
const BATCH_DRAFT_GAP_MS = 2000; // spacing between AI calls, same as the Library page

function fmtDate(iso: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtResponseTime(minutes: number | string | null | undefined) {
  if (minutes === null || minutes === undefined || minutes === "") return "—";
  const m = Number(minutes);
  if (!Number.isFinite(m)) return "—";
  if (m < 60) return `${Math.round(m)}m`;
  if (m < 1440) return `${Math.round(m / 60)}h`;
  return `${Math.round(m / 1440)}d`;
}

const SENTIMENT_COLORS: Record<string, { bg: string; fg: string }> = {
  positive: { bg: "rgba(39, 174, 96, 0.15)", fg: "#86efac" },
  negative: { bg: "rgba(224, 80, 64, 0.15)", fg: "var(--red, #FF8A7A)" },
  neutral: { bg: "rgba(163, 157, 152, 0.15)", fg: "var(--muted, #A39D98)" },
};

function GoogleReviewsTab({ showToast }: { showToast: ShowToast }) {
  const [reviews, setReviews] = useState<GoogleReview[]>([]);
  const [stats, setStats] = useState<GoogleReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [batchLabel, setBatchLabel] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<"all" | "unreplied" | "replied">("all");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [sentimentFilter, setSentimentFilter] = useState("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [brokenPhotos, setBrokenPhotos] = useState<Set<string>>(new Set());

  const [selected, setSelected] = useState<GoogleReview | null>(null);
  const [replyText, setReplyText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const loadStats = async () => {
    try {
      setStats(await getGoogleReviewStats());
    } catch (e) {
      console.error("get_review_stats failed:", e);
    }
  };

  const loadReviews = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setReviews(await listGoogleReviews());
    } catch (e: any) {
      setLoadError(e.message || "Failed to load Google reviews");
    } finally {
      setLoading(false);
    }
    loadStats();
  };

  useEffect(() => {
    loadReviews();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selected) setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  const filtered = useMemo(
    () =>
      reviews.filter((r) => {
        if (statusFilter === "unreplied" && r.reply_text) return false;
        if (statusFilter === "replied" && !r.reply_text) return false;
        if (ratingFilter !== "all" && r.star_rating !== Number(ratingFilter)) return false;
        if (sentimentFilter !== "all" && r.sentiment !== sentimentFilter) return false;
        return true;
      }),
    [reviews, statusFilter, ratingFilter, sentimentFilter]
  );

  // Last 12 months of volume, bar colored by that month's average rating.
  const trend = useMemo(() => {
    const months: Record<string, { total: number; sum: number }> = {};
    for (const r of reviews) {
      if (!r.review_created_at) continue;
      const d = new Date(r.review_created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      months[key] ??= { total: 0, sum: 0 };
      months[key].total++;
      months[key].sum += r.star_rating ?? 0;
    }
    return Object.entries(months)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-12);
  }, [reviews]);

  const patchReview = (id: string, patch: Partial<GoogleReview>) =>
    setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const handleSync = async () => {
    setSyncing(true);
    try {
      const data = await syncGoogleReviews();
      showToast(`Synced ${data.synced ?? 0} reviews (${data.new_reviews ?? 0} new)`);
      await loadReviews();
    } catch (e: any) {
      showToast(e.message || "Sync failed", "err");
    } finally {
      setSyncing(false);
    }
  };

  const handleBatchDrafts = async () => {
    const pending = reviews.filter((r) => !r.reply_text && !r.ai_draft_text && r.comment);
    if (!pending.length) {
      showToast("All reviews already have drafts or replies");
      return;
    }
    const batch = pending.slice(0, BATCH_DRAFT_LIMIT);
    let ok = 0;
    for (let i = 0; i < batch.length; i++) {
      setBatchLabel(`Generating ${i + 1}/${batch.length}…`);
      try {
        const data = await generateGoogleReviewDraft(batch[i].id);
        if (data.draft) {
          patchReview(batch[i].id, { ai_draft_text: data.draft });
          ok++;
        }
      } catch (e) {
        console.error("Batch draft failed:", batch[i].id, e);
      }
      if (i < batch.length - 1) await new Promise((res) => setTimeout(res, BATCH_DRAFT_GAP_MS));
    }
    setBatchLabel(null);
    showToast(`Generated ${ok} of ${batch.length} drafts`, ok === batch.length ? "ok" : "err");
  };

  const openReply = (r: GoogleReview) => {
    setSelected(r);
    setReplyText(r.ai_draft_text || r.reply_text || "");
  };

  const handleGenerate = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const data = await generateGoogleReviewDraft(selected.id);
      setReplyText(data.draft || "");
      patchReview(selected.id, { ai_draft_text: data.draft ?? null });
      showToast("✓ AI draft generated");
    } catch (e: any) {
      showToast(e.message || "Failed to generate draft", "err");
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!selected) return;
    const text = replyText.trim();
    if (!text) return showToast("Reply cannot be empty", "err");
    if (text.length > GOOGLE_REPLY_MAX) return showToast(`Reply too long (max ${GOOGLE_REPLY_MAX} chars)`, "err");
    setSending(true);
    try {
      await sendGoogleReviewReply(selected.id, text);
      patchReview(selected.id, { reply_text: text, reply_updated_at: new Date().toISOString() });
      setSelected(null);
      loadStats();
      showToast("✓ Reply posted to Google");
    } catch (e: any) {
      showToast(e.message || "Failed to send reply", "err");
    } finally {
      setSending(false);
    }
  };

  const unreplied = Number(stats?.unreplied ?? 0);
  const avg = stats?.avg_rating != null && stats.avg_rating !== "" ? Number(stats.avg_rating).toFixed(1) : "—";
  const trendMax = Math.max(1, ...trend.map(([, m]) => m.total));

  return (
    <>
      <div style={styles.statsRow}>
        {(
          [
            ["Total", stats?.total ?? "—", false],
            ["Unreplied", stats ? unreplied : "—", unreplied > 0],
            ["Avg Rating", avg, false],
            ["Avg Response", fmtResponseTime(stats?.avg_response_minutes), false],
          ] as const
        ).map(([label, val, hot]) => (
          <div key={label} style={styles.statCard}>
            <div style={{ ...styles.statVal, ...(hot ? { color: "var(--gold, #F9AF36)" } : {}) }}>{val}</div>
            <div style={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {trend.length >= 2 && (
        <div style={styles.trendBox}>
          <div style={styles.label}>Monthly review volume &amp; rating</div>
          <div style={styles.trendBars}>
            {trend.map(([key, m]) => {
              const avgM = m.sum / m.total;
              const color =
                avgM >= 4.5 ? "var(--green, #27ae60)" : avgM >= 3.5 ? "var(--gold, #F9AF36)" : "var(--red, #FF8A7A)";
              const [y, mo] = key.split("-");
              const label = `${mo}/${y.slice(2)}`;
              return (
                <div key={key} style={styles.trendCol} title={`${label}: ${m.total} reviews, avg ${avgM.toFixed(1)}★`}>
                  <div style={{ ...styles.trendBar, height: `${Math.max(6, (m.total / trendMax) * 100)}%`, background: color }} />
                  <div style={styles.trendLabel}>{label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={styles.googleToolbar}>
        <select style={styles.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} aria-label="Reply status">
          <option value="all">All replies</option>
          <option value="unreplied">Needs reply</option>
          <option value="replied">Replied</option>
        </select>
        <select style={styles.select} value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)} aria-label="Star rating">
          <option value="all">All ratings</option>
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>{n} ★</option>
          ))}
        </select>
        <select style={styles.select} value={sentimentFilter} onChange={(e) => setSentimentFilter(e.target.value)} aria-label="Sentiment">
          <option value="all">All sentiment</option>
          <option value="positive">Positive</option>
          <option value="neutral">Neutral</option>
          <option value="negative">Negative</option>
        </select>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button style={styles.btnAi} onClick={handleBatchDrafts} disabled={!!batchLabel || loading}>
            {batchLabel ?? "✨ Generate All Drafts"}
          </button>
          <button style={styles.btnSecondary} onClick={handleSync} disabled={syncing}>
            {syncing ? "Syncing…" : "↻ Sync from Google"}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loader}>Loading Google reviews…</div>
      ) : loadError ? (
        <div style={styles.emptyState}>{loadError}</div>
      ) : (
        <div style={styles.list}>
          {filtered.map((r) => {
            const name = r.reviewer_name || "Google user";
            const sc = r.sentiment ? SENTIMENT_COLORS[r.sentiment] : null;
            const isExpanded = expanded.has(r.id);
            const long = (r.comment?.length ?? 0) > 280;
            return (
              <div
                key={r.id}
                style={{
                  ...styles.card,
                  borderLeft: r.reply_text ? "3px solid #27ae60" : "3px solid var(--gold, #F9AF36)",
                }}
              >
                <div style={styles.cardTop}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    {r.reviewer_photo_url && !brokenPhotos.has(r.id) ? (
                      <img
                        src={r.reviewer_photo_url}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        style={styles.avatar}
                        onError={() => setBrokenPhotos((s) => new Set(s).add(r.id))}
                      />
                    ) : (
                      <div style={styles.avatarFallback}>{name[0]?.toUpperCase() || "?"}</div>
                    )}
                    <strong style={styles.distilleryName}>{name}</strong>
                  </div>
                  <span style={styles.date}>{fmtDate(r.review_created_at)}</span>
                </div>
                <span style={styles.stars}>{renderStars(r.star_rating ?? 0)}</span>

                {r.comment ? (
                  <>
                    <p style={{ ...styles.reviewBody, ...(long && !isExpanded ? styles.clamped : {}) }}>{r.comment}</p>
                    {long && (
                      <button
                        style={styles.linkBtn}
                        onClick={() =>
                          setExpanded((s) => {
                            const n = new Set(s);
                            n.has(r.id) ? n.delete(r.id) : n.add(r.id);
                            return n;
                          })
                        }
                      >
                        {isExpanded ? "Show less" : "Show more"}
                      </button>
                    )}
                  </>
                ) : (
                  <p style={{ ...styles.reviewBody, fontStyle: "italic", color: "var(--muted, #A39D98)" }}>
                    Rating only — no comment
                  </p>
                )}

                {r.reply_text && (
                  <div style={styles.responseBubble}>
                    <div style={styles.responseHeader}>Our reply:</div>
                    <div style={styles.responseText}>{r.reply_text}</div>
                  </div>
                )}

                <div style={styles.actionsRow}>
                  {sc && (
                    <span style={{ ...styles.statusBadge, background: sc.bg, color: sc.fg }}>{r.sentiment}</span>
                  )}
                  <span
                    style={{
                      ...styles.statusBadge,
                      background: r.reply_text ? "rgba(39, 174, 96, 0.15)" : "rgba(249, 175, 54, 0.15)",
                      color: r.reply_text ? "#86efac" : "var(--gold, #F9AF36)",
                    }}
                  >
                    {r.reply_text ? "Replied" : r.ai_draft_text ? "Draft ready" : "Needs reply"}
                  </span>
                  <button style={styles.btnRespond} onClick={() => openReply(r)}>
                    {r.reply_text ? "Edit Reply" : "Reply"}
                  </button>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={styles.emptyState}>
              {reviews.length === 0 ? "No Google reviews yet — try Sync from Google." : "No reviews match these filters."}
            </div>
          )}
        </div>
      )}

      {selected && (
        <div style={styles.modalBackdrop} onClick={() => !sending && setSelected(null)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Reply to {selected.reviewer_name || "Google user"}</h2>
              <button style={styles.modalClose} onClick={() => setSelected(null)} aria-label="Close" disabled={sending}>
                &times;
              </button>
            </div>

            <div style={styles.originalReviewQuote}>
              <div style={styles.stars}>{renderStars(selected.star_rating ?? 0)}</div>
              <div style={styles.quoteText}>{selected.comment ? `"${selected.comment}"` : "Rating only — no comment"}</div>
            </div>

            {selected.reply_text && (
              <div style={styles.responseBubble}>
                <div style={styles.responseHeader}>Current public reply:</div>
                <div style={styles.responseText}>{selected.reply_text}</div>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label style={styles.label} htmlFor="google-reply">
                Public reply — posts to Google
              </label>
              <button type="button" style={styles.btnAi} disabled={generating || sending} onClick={handleGenerate}>
                {generating ? "Generating..." : "✨ Generate AI Reply"}
              </button>
            </div>

            <textarea
              id="google-reply"
              rows={6}
              placeholder="Write your reply..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              style={styles.textarea}
            />
            <div style={{ ...styles.date, textAlign: "right", ...(replyText.length > GOOGLE_REPLY_MAX ? { color: "var(--red)" } : {}) }}>
              {replyText.length} / {GOOGLE_REPLY_MAX}
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.btnSecondary} onClick={() => setSelected(null)} disabled={sending}>
                Cancel
              </button>
              <button style={styles.btnPrimary} onClick={handleSend} disabled={sending || !replyText.trim()}>
                {sending ? "Posting..." : selected.reply_text ? "Update Reply" : "Send Reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "24px 20px 60px",
    maxWidth: 1000,
    margin: "0 auto",
    color: "var(--text, #F7F5F0)",
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 400,
    margin: "0 0 4px",
    color: "var(--text, #F7F5F0)",
  },
  subtitle: {
    color: "var(--muted, #A39D98)",
    fontSize: 14,
    margin: 0,
  },
  toast: {
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid",
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 600,
  },
  toolbar: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginBottom: 20,
  },
  filterPills: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  pillBtn: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  pillBtnActive: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    borderColor: "var(--gold, #F9AF36)",
  },
  searchInput: {
    width: "100%",
    padding: "10px 14px",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    color: "var(--text, #F7F5F0)",
    fontSize: 14,
    outline: "none",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  card: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 10,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  stars: {
    color: "var(--gold, #F9AF36)",
    fontSize: 15,
    letterSpacing: 2,
    marginRight: 8,
  },
  distilleryName: {
    fontSize: 15,
    color: "var(--text, #F7F5F0)",
    fontFamily: "Georgia, serif",
  },
  metaRight: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  statusBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 10,
    textTransform: "uppercase",
  },
  date: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
  },
  byline: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
  },
  reviewBody: {
    fontSize: 14,
    color: "var(--text, #F7F5F0)",
    lineHeight: 1.5,
    margin: "4px 0",
  },
  responseBubble: {
    background: "rgba(249, 175, 54, 0.04)",
    borderLeft: "2px solid var(--gold, #F9AF36)",
    padding: "8px 12px",
    borderRadius: "0 6px 6px 0",
    margin: "4px 0",
  },
  responseHeader: {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
    marginBottom: 4,
  },
  responseText: {
    fontSize: 13,
    color: "var(--text, #F7F5F0)",
    lineHeight: 1.4,
  },
  actionsRow: {
    display: "flex",
    gap: 8,
    marginTop: 6,
    flexWrap: "wrap",
  },
  btnPublish: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.3)",
    color: "#86efac",
    padding: "6px 12px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnFlag: {
    background: "rgba(224, 80, 64, 0.15)",
    border: "1px solid rgba(224, 80, 64, 0.3)",
    color: "var(--red, #FF8A7A)",
    padding: "6px 12px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnHide: {
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    padding: "6px 12px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnRespond: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: 0,
    padding: "6px 14px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    marginLeft: "auto",
  },
  loader: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #A39D98)",
  },
  emptyState: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #A39D98)",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 10,
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 100,
  },
  modalCard: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    width: "100%",
    maxWidth: 580,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    boxShadow: "0 20px 50px rgba(0,0,0,0.8)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 17,
    margin: 0,
    color: "var(--text, #F7F5F0)",
  },
  modalClose: {
    background: "transparent",
    border: 0,
    color: "var(--muted, #A39D98)",
    fontSize: 22,
    cursor: "pointer",
  },
  originalReviewQuote: {
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    padding: 12,
  },
  quoteText: {
    fontSize: 13,
    color: "var(--text, #F7F5F0)",
    fontStyle: "italic",
    marginTop: 4,
    lineHeight: 1.4,
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--muted, #A39D98)",
  },
  textarea: {
    padding: "10px 12px",
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    color: "var(--text, #F7F5F0)",
    fontSize: 13,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    lineHeight: 1.5,
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 8,
  },
  btnPrimary: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: 0,
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnSecondary: {
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnAi: {
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid rgba(249, 175, 54, 0.35)",
    color: "var(--gold, #F9AF36)",
    padding: "4px 10px",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  sourceTabs: {
    display: "flex",
    gap: 4,
    borderBottom: "1px solid var(--border, #231D19)",
    marginBottom: 20,
  },
  sourceTab: {
    background: "transparent",
    border: 0,
    borderBottom: "2px solid transparent",
    color: "var(--muted, #A39D98)",
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  sourceTabActive: {
    color: "var(--gold, #F9AF36)",
    borderBottomColor: "var(--gold, #F9AF36)",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: 10,
    marginBottom: 16,
  },
  statCard: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 10,
    padding: "12px 14px",
  },
  statVal: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    color: "var(--text, #F7F5F0)",
  },
  statLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--muted, #A39D98)",
  },
  trendBox: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 16,
  },
  trendBars: {
    display: "flex",
    alignItems: "flex-end",
    gap: 6,
    height: 80,
    marginTop: 8,
  },
  trendCol: {
    flex: 1,
    maxWidth: 40,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  trendBar: {
    width: "100%",
    borderRadius: 3,
    opacity: 0.75,
  },
  trendLabel: {
    fontSize: 9,
    color: "var(--muted, #A39D98)",
    marginTop: 3,
  },
  googleToolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
    marginBottom: 16,
  },
  select: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    color: "var(--text, #F7F5F0)",
    padding: "7px 10px",
    borderRadius: 6,
    fontSize: 12,
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },
  avatarFallback: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  clamped: {
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  linkBtn: {
    background: "transparent",
    border: 0,
    color: "var(--gold, #F9AF36)",
    fontSize: 12,
    padding: 0,
    cursor: "pointer",
    alignSelf: "flex-start",
  },
};
